import React, { useState, useEffect } from 'react';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui-elements';
import { useAuthStore } from '@/lib/auth-store';
import { useToast } from '@/hooks/use-toast';
import {
  MessageSquare, Plus, Trash2, TestTube, MapPin,
  CheckCircle2, Clock, ExternalLink, ShieldCheck,
} from 'lucide-react';

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

interface MaskedIntegration {
  id: string;
  provider: string;
  label: string;
  locationUid: string | null;
  connected: boolean;
  createdAt: string;
}

function useApi() {
  const { token } = useAuthStore();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const get = async (path: string) => {
    const r = await fetch(`${BASE}/api${path}`, { headers: { ...authHeader } });
    if (!r.ok) throw new Error((await r.json()).error ?? r.statusText);
    return r.json();
  };
  const post = async (path: string, body: unknown) => {
    const r = await fetch(`${BASE}/api${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader },
      body: JSON.stringify(body),
    });
    if (!r.ok) throw new Error((await r.json()).error ?? r.statusText);
    return r.json();
  };
  const del = async (path: string) => {
    const r = await fetch(`${BASE}/api${path}`, { method: 'DELETE', headers: { ...authHeader } });
    if (!r.ok && r.status !== 204) throw new Error(r.statusText);
  };

  return { get, post, del };
}

const inputCls = "w-full bg-muted/50 border border-border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20";
const monoInputCls = `${inputCls} font-mono`;

export function MessagingIntegrations() {
  const { toast } = useToast();
  const api = useApi();

  const [integrations, setIntegrations] = React.useState<MaskedIntegration[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [showForm, setShowForm] = useState(false);
  const [label, setLabel] = useState('');
  const [locationUid, setLocationUid] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  // Handle OAuth redirect-back: if we're inside a popup, notify opener and close
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    const error = params.get('error');
    if ((connected || error) && window.opener) {
      // Running inside the OAuth popup — message the parent then close
      try {
        window.opener.postMessage(
          { type: 'podium_oauth', connected: connected === 'true', error: error ?? null },
          window.location.origin,
        );
      } catch {}
      window.close();
      return;
    }
    // Normal full-page flow (fallback when popup was blocked)
    if (connected === 'true') {
      toast({ title: 'Podium connected!', description: 'Your Podium location is now linked.' });
      window.history.replaceState({}, '', window.location.pathname);
      load();
    } else if (error) {
      toast({ title: 'Podium connection failed', description: decodeURIComponent(error), variant: 'destructive' });
      window.history.replaceState({}, '', window.location.pathname);
      load();
    } else {
      load();
    }
  }, []);

  // Listen for messages posted by the OAuth popup
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type !== 'podium_oauth') return;
      if (e.data.connected) {
        toast({ title: 'Podium connected!', description: 'Your Podium location is now linked.' });
        load();
        resetForm();
      } else if (e.data.error) {
        toast({ title: 'Podium connection failed', description: decodeURIComponent(e.data.error), variant: 'destructive' });
      }
      setConnecting(false);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const load = async () => {
    try {
      const data = await api.get('/messaging-integrations');
      setIntegrations(data);
    } catch (err: any) {
      toast({ title: 'Error loading integrations', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setLabel('');
    setLocationUid('');
  };

  const handleConnect = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!label || !locationUid) return;
    setConnecting(true);
    try {
      const returnUrl = window.location.origin + window.location.pathname.split('?')[0];
      const result = await api.post('/messaging-integrations/podium/initiate', { label, locationUid, returnUrl });

      // Open Podium authorization in a popup so the user can see the consent page
      // and the main page stays open and auto-refreshes on completion
      const w = 600, h = 700;
      const left = Math.round(window.screenX + (window.outerWidth - w) / 2);
      const top = Math.round(window.screenY + (window.outerHeight - h) / 2);
      const popup = window.open(
        result.authorizeUrl,
        'podium_oauth',
        `width=${w},height=${h},left=${left},top=${top},scrollbars=yes,resizable=yes`,
      );

      if (!popup || popup.closed) {
        // Popup was blocked — fall back to full-page redirect
        window.location.href = result.authorizeUrl;
        return;
      }

      // Poll for popup closure (handles cases where user closes without completing)
      const poll = setInterval(() => {
        if (popup.closed) {
          clearInterval(poll);
          setConnecting(false);
          load();
        }
      }, 600);
    } catch (err: any) {
      toast({ title: 'Error starting connection', description: err.message, variant: 'destructive' });
      setConnecting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Remove this messaging integration?')) return;
    setDeleting(id);
    try {
      await api.del(`/messaging-integrations/${id}`);
      setIntegrations((prev) => prev.filter((i) => i.id !== id));
      toast({ title: 'Integration removed' });
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setDeleting(null);
    }
  };

  const handleTest = async (id: string) => {
    setTesting(id);
    try {
      const result = await api.post(`/messaging-integrations/${id}/test`, {});
      if (result.success) {
        toast({ title: 'Connection successful', description: result.locationName ?? 'Connected to Podium' });
        load();
      } else {
        toast({ title: 'Connection failed', description: result.error ?? 'Unknown error', variant: 'destructive' });
      }
    } catch (err: any) {
      toast({ title: 'Test error', description: err.message, variant: 'destructive' });
    } finally {
      setTesting(null);
    }
  };

  return (
    <Layout>
      <div className="flex justify-between items-end mb-8">
        <div>
          <h1 className="text-3xl font-display text-gradient mb-2">Messaging Integrations</h1>
          <p className="text-muted-foreground">Connect your Podium location to enable SMS in customer automations.</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="gap-2" disabled={showForm}>
          <Plus className="w-4 h-4" /> Add Podium Location
        </Button>
      </div>

      {showForm && (
        <div className="glass-panel rounded-2xl p-6 mb-6 border border-primary/20">
          <h3 className="text-lg font-bold mb-1 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-primary" /> Connect Podium Location
          </h3>
          <p className="text-xs text-muted-foreground mb-5">
            You'll be redirected to Podium to authorize access. Your{' '}
            <strong className="text-muted-foreground">Location UID</strong> can be found in{' '}
            Podium → Settings → Locations (shown in the location's URL or details).
          </p>
          <form onSubmit={handleConnect} className="space-y-4">
            <div>
              <label className="block text-sm text-muted-foreground mb-1">Label</label>
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. 4x4OC Podium"
                className={inputCls}
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1 flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" /> Location UID
              </label>
              <input
                value={locationUid}
                onChange={(e) => setLocationUid(e.target.value)}
                placeholder="Podium location UID"
                className={monoInputCls}
              />
            </div>
            {/* Permissions review — shown before the user authorises */}
            <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-2">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="w-4 h-4 text-primary flex-shrink-0" />
                <span className="text-sm font-semibold text-foreground">Permissions this connection will request</span>
              </div>
              {[
                { icon: '📍', label: 'View your Podium locations' },
                { icon: '📨', label: 'Read and send messages from your location' },
                { icon: '👤', label: 'View and create Podium contacts' },
                { icon: '📋', label: 'Read SMS templates' },
                { icon: '📣', label: 'Read campaigns and send campaign messages' },
              ].map(({ icon, label }) => (
                <div key={label} className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{icon}</span>
                  <span>{label}</span>
                </div>
              ))}
              <p className="text-[10px] text-slate-600 pt-1">
                If you have already authorized this app in Podium, it will reconnect automatically without showing a consent screen.
                To review or revoke permissions, go to your Podium account → Settings → Connected Apps.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="submit"
                isLoading={connecting}
                disabled={!label || !locationUid}
                className="gap-2"
              >
                {!connecting && <ExternalLink className="w-4 h-4" />}
                {connecting ? 'Connecting to Podium…' : 'Connect with Podium'}
              </Button>
              <Button type="button" variant="ghost" onClick={resetForm} disabled={connecting}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="space-y-4 animate-pulse">
          {[1, 2].map((i) => <div key={i} className="h-20 bg-muted/50 rounded-2xl" />)}
        </div>
      ) : integrations.length === 0 && !showForm ? (
        <div className="glass-panel rounded-3xl p-16 text-center border-dashed border-border">
          <MessageSquare className="w-16 h-16 mx-auto mb-6 text-slate-600" />
          <h3 className="text-xl font-display mb-2">No Podium locations connected</h3>
          <p className="text-muted-foreground mb-8 max-w-md mx-auto">
            Connect a Podium location to enable SMS actions in your customer automations.
          </p>
          <Button onClick={() => setShowForm(true)}>Add Podium Location</Button>
        </div>
      ) : (
        <div className="space-y-4">
          {integrations.map((integ) => (
            <div key={integ.id} className="glass-panel rounded-2xl p-5 border border-border flex items-center gap-6">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 border border-blue-500/20 flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-5 h-5 text-blue-400" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-0.5">
                  <p className="font-bold text-foreground">{integ.label}</p>
                  <span className="px-2 py-0.5 text-[10px] uppercase font-bold tracking-wider rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/20">
                    {integ.provider}
                  </span>
                  {integ.connected ? (
                    <span className="flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase font-bold tracking-wider rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">
                      <CheckCircle2 className="w-3 h-3" /> Connected
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase font-bold tracking-wider rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/20">
                      <Clock className="w-3 h-3" /> Pending auth
                    </span>
                  )}
                </div>
                {integ.locationUid && (
                  <p className="text-xs text-muted-foreground font-mono mt-1">
                    Location UID: <span className="text-muted-foreground">{integ.locationUid}</span>
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {integ.connected ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="gap-2"
                    onClick={() => handleTest(integ.id)}
                    isLoading={testing === integ.id}
                  >
                    {testing !== integ.id && <TestTube className="w-3.5 h-3.5" />}
                    Test
                  </Button>
                ) : (
                  <span className="text-xs text-amber-400/80 italic">Authorization pending</span>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                  onClick={() => handleDelete(integ.id)}
                  isLoading={deleting === integ.id}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
}
